<script lang="ts" setup>
  import CommunitiesSideBar from '@/components/common/NavBar/web/CommunitiesSideBar.vue'
  import CommunitiesTopNavBar from '@/components/common/NavBar/web/CommunitiesTopNavBar.vue'
  import TopNavBar from '@/components/common/NavBar/web/TopNavBar.vue'
  import PrivateChannelTooltip from '@/components/common/tooltip/PrivateChannelTooltip.vue'
  import PrivateCourseTooltip from '@/components/common/tooltip/PrivateCourseTooltip.vue'
  import RestrictedEventTooltip from '@/components/common/tooltip/RestrictedEventTooltip.vue'
  import MoveToChannelModal from '@/components/posts/MoveToChannelModal.vue'
  import { app } from '@/composition/app'
  import { ChannelState } from '@/composition/channels'
  import { GroupState } from '@/composition/groups'
  import { InvitationState } from '@/composition/invitations'
  import { Theme } from '@/composition/themes'
  import { UserState } from '@/composition/users'
  import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
  import { initiateCustomTags, initiateCustomTagsCommunities } from '@/helper/customCode.helper'
  import { emitter } from '@/main'
  import { MemberStatus, MembershipType } from '@/models/Groups'
  import { IPosts } from '@/models/Posts'
  import { RouterHelper } from '@/router/router.helper'
  import restAgent from '@/service/restAgent'
  import { useGlobalStore } from '@/store/app'
  import { handleColorsInitialization } from '@/util/colorTokens'
  import { checkIsSideBackgroundDark } from '@/util/colorUtils'
  import { BottomNavBarMobile, isAppLoading, setAppLoading } from '@ghl/clientportal-core'
  import { addChatWidgetPositioningScript, isGokollab, isMobileOs, isPwa } from '@ghl/clientportal-core/init'
  import { UIAvatar, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { useFavicon } from '@vueuse/core'
  import { useHead } from '@vueuse/head'
  import postscribe from 'postscribe'
  import { computed, defineAsyncComponent, h, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { openChatWindow, truncateString } from '../components/helper'
  import { ConversationState } from '../composition/conversations'
  import { useCommunitiesToastNotification } from '../helper/communitiesToastNotification.helper'
  import { getCanonicalUrl, hidePageLoaderAnimation } from '../util/helper'
  import LoginBanner from './mobile/LoginBanner.vue'
  import FAVICON from '/favicon-safari.png'

  const { t } = useI18n()

  // Helper function to conditionally add chat widget positioning script
  const addChatWidgetPositioningIfNeeded = () => {
    if (isPwa() || app.state.isMobile) {
      addChatWidgetPositioningScript()
    }
  }

  const MobileSideNav = defineAsyncComponent(() => import('./MobileSideNav.vue'))

  const MandatedInfoModal = defineAsyncComponent(() => import('@/components/common/MandatedInfoModal.vue'))
  const TopNavBarMobile = defineAsyncComponent(() => import('@/components/common/NavBar/mobile/TopNavBarMobile.vue'))
  const UIConfirmation = defineAsyncComponent(() => import('@/components/common/modal/UIConfirmation.vue'))
  const PaymentRedirectionConfirmation = defineAsyncComponent(
    () => import('@/components/groups/payments/PaymentRedirectionConfirmation.vue')
  )
  const ChatViewIndex = defineAsyncComponent(() => import('@/components/chats/ChatViewIndex.vue'))
  const AnimatedLoader = defineAsyncComponent(() => import('@/components/common/animations/AnimatedLoader.vue'))
  const GroupSettings = defineAsyncComponent(() => import('@/components/groups/settings/GroupSettings.vue'))
  const LoginModal = defineAsyncComponent(() => import('@/components/login/LoginModal.vue'))
  const currentFavicon = useFavicon()
  const route = useRoute()
  const router = useRouter()
  const isLoading = ref(true)
  const options = ref([])
  const showStickyHeader = ref(true)
  const selectedGroup = ref()
  const selectedGroupObj = ref()
  const transitionName = ref()
  const showRouteAccessInfo = ref(false)
  const notification = useCommunitiesToastNotification()
  const showMovePostToChannelModal = ref(false)
  const postToMove = ref<IPosts | null>(null)
  const showLoginModal = ref(false)
  const appStore = useGlobalStore()
  const showMandatedInfoModal = computed(() => UserState.state.showMandatoryInfoModal)
  // Global: Add canonical URL to the head
  useHead({
    link: [
      {
        rel: 'canonical',
        href: getCanonicalUrl(route),
      },
    ],
  })

  // Listen to route change and update canonical URL
  watch(route, () => {
    useHead({
      link: [
        {
          rel: 'canonical',
          href: getCanonicalUrl(route),
        },
      ],
    })
    // Update conversationId when route changes
    conversationId.value = route?.query?.conversationId
  })

  function handleOpenMovePostToChannelModal(post: any) {
    postToMove.value = post
    showMovePostToChannelModal.value = true
  }

  // Pass showStickyHeader to child components using provide
  provide('showStickyHeader', showStickyHeader)

  const hideTopNavbarDueToRoute = computed(() => {
    return ['NotificationMobileView', 'clientportal-home', 'chat-view'].includes(route.name as string)
  })

  const hideCommunitiesTopNavbar = computed(() => {
    return [
      'communities-more',
      'clientportal-home',
      'profile-container',
      'communities-settings',
      'documents-and-payments',
      'no-communities-mobile',
      'conversations',
      'chat-view',
      'to-review',
      'my-courses',
      'global-user-profile',
    ].includes(route.name as string)
  })

  const routesWithNoTopMargin = computed(() => {
    /**
     * Excludes the 'communities-more' page from having a top margin applied.
     * This is necessary because the 'communities-more' page is rendered from the ClientPortal-Core.
     */
    return ['communities-more', 'profile-container', 'documents-and-payments', 'conversations', 'chat-view'].includes(route.name as string)
  })

  const getChatWidget = async () => {
    if (!appStore.chatWidgetSettings?.permissions?.communities || route.name === 'clientportal-home' || route.name === 'profile-container') {
      postscribe(`#clientportal-chatWidget`, '')
      const chatWidget = document.querySelector('chat-widget')
      if (chatWidget) chatWidget.remove()
      return
    }

    const widgetId = appStore.chatWidgetSettings?.widgetId
    const hasValidWidget = Boolean(widgetId && widgetId !== 'none')

    if (hasValidWidget) {
      try {
        const response = await restAgent.ClientPortal.getChatWidgetConfig(appStore.locationId, widgetId)

        postscribe(`#clientportal-chatWidget`, response?.code || '', {
          done: () => {
            const applyStyles = (element: Element) => {
              ;(element as HTMLElement).style.position = 'relative'
              ;(element as HTMLElement).style.zIndex = '50'
            }

            // Try immediate selection
            const chatWidget = document.querySelector('chat-widget')
            if (chatWidget) {
              applyStyles(chatWidget)
              addChatWidgetPositioningIfNeeded()
              return
            }

            // Watch for chat widget creation
            const observer = new MutationObserver(mutations => {
              for (const mutation of mutations) {
                for (const node of Array.from(mutation.addedNodes)) {
                  if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as Element
                    const widget = element.tagName?.toLowerCase() === 'chat-widget' ? element : element.querySelector('chat-widget')

                    if (widget) {
                      applyStyles(widget)
                      observer.disconnect()
                      addChatWidgetPositioningIfNeeded()
                      return
                    }
                  }
                }
              }
            })

            observer.observe(document.body, { childList: true, subtree: true })

            // Cleanup after 10 seconds
            setTimeout(() => observer.disconnect(), 10000)
          },
        })
      } catch (error) {
        console.error('error', error)
      }
    } else {
      postscribe(`#clientportal-chatWidget`, '')
      const chatWidget = document.querySelector('chat-widget')
      if (chatWidget) {
        chatWidget.remove()
      }
    }
  }

  const conversationId = ref()

  onMounted(async () => {
    emitter.on('open-move-post-to-channel-modal', handleOpenMovePostToChannelModal)
    emitter.on('access:private-group-unauthorized', handleUnauthorizedAccess)
    emitter.on('app:loaded', handleAppLoaded)
    conversationId.value = route?.query?.conversationId
    loadGroup()
    const groupSwitcher = GroupState.state.groupList
    options.value = groupSwitcher.map((group: any) => {
      const groupOption = {
        id: group.name,
        key: group.slug,
        label: truncateString(group.name, 30),
        imageUrl: replaceBucketUrlWithCdnUrl(group?.branding?.logo),
        icon: () => {
          const groupAvatar = h(UIAvatar, {
            style: {
              margin: '8px',
            },
            size: 24,
            class: 'font-medium text-communities-font-secondary bg-communities-surface text-md',
            src: replaceBucketUrlWithCdnUrl(group?.branding?.logo),
          })
          return groupAvatar
        },
      }
      if (route.params.groupSlug === group.slug) {
        // groupOption['disabled'] = true
        groupOption['props'] = {
          class: 'bg-gray-50 text-sm text-gray-700 rounded-xl',
        }
        selectedGroupObj.value = groupOption
        selectedGroup.value = groupOption.key
      } else {
        groupOption['props'] = {
          class: 'text-sm text-gray-700',
        }
      }
      return groupOption
    })
    if (route.name === 'communities' && !conversationId.value) {
      router.push({
        name: 'home',
        params: {
          groupSlug: route?.params?.groupSlug || GroupState.state?.groupList[0]?.slug,
        },
      })
    }

    if (app.state.isMobile || isMobileOs()) {
      // Select the body element
      const bodyElement = document.body
      // Apply user-select: none; to the body
      bodyElement.style.userSelect = 'none'

      // If the screen is considered mobile, apply the viewport meta tag to prevent zooming
      const viewportMeta = document.createElement('meta')
      viewportMeta.name = 'viewport'
      viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
      document.head.appendChild(viewportMeta)
    }
  })

  onBeforeUnmount(() => {
    emitter.off('open-move-post-to-channel-modal', handleOpenMovePostToChannelModal)
    emitter.off('access:private-group-unauthorized', handleUnauthorizedAccess)
    emitter.off('app:loaded', handleAppLoaded)
  })

  watch(
    () => appStore.chatWidgetSettings?.permissions?.communities,
    (newVal: boolean, oldVal: boolean) => {
      if (newVal && !oldVal) {
        getChatWidget()
      }
    }
  )
  watch(
    () => route.name,
    routeName => {
      showStickyHeader.value = !(window.scrollY === 0 && routeName === 'home')
    }
  )

  async function handleEmailInvitations(invitationId) {
    if (route.name === 'group-invitations' && !GroupState.state.isGroupAdmin) {
      await router.push({
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
      })
    }

    try {
      if (invitationId) {
        try {
          const { magicLink } = await InvitationState.acceptInvitation(invitationId)
          window.location.href = magicLink
          return true
        } catch (error: any) {
          console.error(error?.response?.data?.message)
          notification.create({
            type: 'error',
            title: `Unable to join the group`,
            content: error?.response?.data?.message,
            duration: 3000,
          })
          return false
        }
      } else {
        return false
      }
    } catch {
      return false
    }
  }

  async function loadGroup() {
    try {
      isLoading.value = true
      setAppLoading(true)
      app.createApp(route, router)

      const invitationRedirection = await handleEmailInvitations(route.query.invitationId)
      if (invitationRedirection) {
        return true
      }

      if (route.query.joinGroup) {
        try {
          if (GroupState.state?.groupData?.settings?.membership?.membershipType !== MembershipType.PAID) {
            await GroupState.joinGroup(GroupState.state.groupData._id)
          }
        } catch (error) {
          console.error(error)
        }
      }
      isLoading.value = false
    } catch {
      if (GroupState.state?.groupList?.length) {
        router.push({
          name: 'home',
          params: {
            groupSlug: route?.params?.groupSlug || GroupState.state?.groupList[0]?.slug,
          },
        })
      } else {
        router.push({ name: 'no-communities' })
      }
    }
  }

  watch(
    () => GroupState.state.fetchingGroupData,
    async newVal => {
      if (conversationId.value) {
        openChatWindow(undefined, router, conversationId.value)
      }

      if (!newVal) {
        //Set site branding
        if (!isGokollab()) {
          if (GroupState.state.groupData?.branding?.favicon) {
            currentFavicon.value = GroupState.state.groupData?.branding?.favicon
          } else {
            currentFavicon.value = FAVICON
          }
        }

        if (GroupState.state.groupData?.name) {
          window.document.title = GroupState.state.groupData?.name
        }

        // Handle theme update
        checkIsSideBackgroundDark()
        handleColorsInitialization()

        if (GroupState?.state?.groupData?.settings?.customCode) {
          initiateCustomTagsCommunities({
            ...GroupState?.state?.groupData?.settings?.customCode,
            customCode: route.query.customCode,
          })
        }

        // fetch channels for group if its private check if user is logged in
        if (
          (isGokollab() && GroupState.state?.userStatus === MemberStatus.ACTIVE) ||
          GroupState.state.groupData?.settings?.groupType != 'Private' ||
          (GroupState.state.role && GroupState.state?.userStatus === MemberStatus.ACTIVE)
        ) {
          ChannelState.fetchChannels()
        }
      }
    }
  )

  watch(
    () => app.state.fetching,
    async newVal => {
      if (!newVal) {
        await Promise.all([
          RouterHelper.handleBannedUser(route, router),
          RouterHelper.handlePrivateGroups(route, router),
          RouterHelper.handleInactiveGroups(route, router),
        ])
      }
    }
  )

  function handleAppLoaded() {
    if (!UserState.state.isCurrentUserFetching) {
      setAppLoading(false)
      setTimeout(() => {
        hidePageLoaderAnimation()
      }, 100)
    }
  }

  const loadUserGroupInfo = async () => {
    if (UserState.state.currentUser) {
      UserState.state.isCurrentUserGroupInfoFetching = true
      const groupInfo = await UserState.getUserByContactId(UserState.state.currentUser.contactId)
      UserState.state.currenUserGroupInfo = groupInfo
      UserState.state.isCurrentUserGroupInfoFetching = false
    }
  }

  watch(
    () => route.params,
    async (newParams, oldParams) => {
      if (newParams.groupSlug !== oldParams.groupSlug) {
        // resetGroupData()
        await loadGroup()
      }
      loadUserGroupInfo()
    }
  )

  // Load group level info for current user
  watch(
    () => UserState.state.currentUser,
    async () => {
      loadUserGroupInfo()
    }
  )

  watch(
    () => app.state.locationData,
    (newLocationData, oldLocationData) => {
      // Only run when locationData actually changes and has meaningful data
      if (newLocationData && newLocationData !== oldLocationData) {
        initiateCustomTags(newLocationData)
      }
    },
    {
      deep: true, // Watch nested object changes
      immediate: false, // Don't run on component mount
    }
  )

  const routes = [
    'clientportal-home',
    'home',
    'learning',
    'learning-home',
    'member-list',
    'group-member-list',
    'leaderboard',
    'NotificationMobileView',
    'notification-mobile',
    'profile-container',
    'no-communities-mobile',
    'private-group',
    'conversations',
  ]

  const bottomNavBarRouteConfig = [
    {
      name: 'Home',
      routeName: ['clientportal-home'],
    },

    {
      name: 'Communities',
      routeName: [
        'home',
        'channel-overview',
        'channel-post-view',
        'group-post-view',
        'content-unavailable',
        'group-home-search',
        'group-search-post-view',
        'channel-search',
        'channel-search-post-view',
        'learning-home',
        'group-member-list',
        'events',
        'leaderboard',
        'about',
        'private-group',
        'inactive-group',
        'no-communities-mobile',
      ],
    },
    {
      name: 'Courses',
      // TODO: JA: Update post Bharat's changes
      routeName: ['my-courses'],
    },
    {
      name: 'More',
      routeName: ['communities-more', 'profile-container', 'documents-and-payments'],
    },
  ]

  watch(
    () => route.fullPath,
    (to, from) => {
      const toPath = to.split('/').pop() || ''
      const fromPath = from.split('/').pop() || ''
      const toIndex = routes.indexOf(toPath)
      const fromIndex = routes.indexOf(fromPath)
      if (fromIndex > toIndex) {
        transitionName.value = 'slide-right'
      } else {
        transitionName.value = 'slide-left'
      }
    }
  )

  function closeChat(index) {
    ConversationState.state.activeChatWindows.splice(index, 1)
  }
  const activeChats = computed(() => {
    return ConversationState.state.activeChatWindows
  })

  function handlePostUpdated(post: IPosts) {
    postToMove.value!.channelId = post.channelId
    postToMove.value!.channelName = post.channelName
    postToMove.value!.channelSlug = post.channelSlug
  }

  const handleUnauthorizedAccess = data => {
    notification.create({
      title: data.message,
      type: 'warning',
      duration: 2000,
    })
  }

  function handleLoginAction() {
    // Close the login modal after successful login
    appStore.closeLoginModal()
    // Redirect or refresh as needed after login
  }

  // Watch function for GroupState.state
  watch(
    () => route.name,
    async () => {
      if (route.name === 'clientportal-home' || route.name === 'profile-container' || route.name === 'home') {
        await getChatWidget()
      }
    },
    { deep: true }
  )
</script>
<template>
  <UIContentWrap v-if="isLoading || (app.state.isPwa && isAppLoading())" class="outer-container w-full !bg-communities-background">
    <AnimatedLoader size="large" class="flex h-[80vh] min-w-48 items-center" />
  </UIContentWrap>
  <UIContentWrap
    v-else
    :full-screen="true"
    class="outer-container max-h-screen w-full"
    :class="
      Theme.state.isSideBarBackgroundDark && Theme.state.windowGradient
        ? 'bg-communities-sidebar-background-gradient'
        : 'bg-communities-sidebar-background'
    "
  >
    <!-- Web Layout -->
    <div v-if="!app.state.isMobile">
      <TopNavBar />
      <div class="flex justify-center" :class="!app.state.isMobile && 'mt-18'">
        <div class="communities-container w-full">
          <UIContentWrap class="outer-container full-screen lg:mobile-padding w-full max-w-full pb-2">
            <div class="relative flex justify-between">
              <CommunitiesSideBar class="sidebar z-40 max-w-[300px]" />

              <div class="flex w-full flex-auto flex-col justify-center gap-4">
                <CommunitiesTopNavBar class="box_shadow_communities_nav" />
                <div class="flex justify-center bg-communities-surface">
                  <div class="mx-5 min-h-screen w-full max-w-[1158px] pt-[62px]" :class="app.state.readingMode && 'mx-5'">
                    <router-view />
                  </div>
                </div>
              </div>
            </div>
          </UIContentWrap>
        </div>
      </div>
    </div>
    <!-- Mobile Layout -->
    <div v-else>
      <LoginBanner v-if="appStore.shouldShowLoginBanner" />

      <Transition name="fade" appear>
        <TopNavBarMobile v-if="!hideTopNavbarDueToRoute" />
      </Transition>
      <CommunitiesTopNavBar v-if="!hideCommunitiesTopNavbar" class="box_shadow_communities_nav" />

      <!-- <div
        class="flex justify-center"
        :class="{
          'mt-24': !hideCommunitiesTopNavbar, // add top margin if the communities top navbar is not hidden
        }"
      > -->
      <div class="flex justify-center">
        <div
          id="communities-main-container"
          class="communities-container w-full"
          :class="{
            'mt-10': !routesWithNoTopMargin && appStore.isAuthenticated,
            'mt-[94px]': !routesWithNoTopMargin && appStore.shouldShowLoginBanner,
          }"
        >
          <router-view v-slot="{ Component }">
            <transition :name="transitionName">
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </div>
      <BottomNavBarMobile
        v-if="!GroupState.state.routesWithNoBottomNavbar.includes(route.name) && !appStore.shouldShowLoginBanner"
        class="fixed bottom-0 z-50 flex w-full justify-center"
        :route-config="bottomNavBarRouteConfig"
      />
    </div>

    <MandatedInfoModal v-if="showMandatedInfoModal" :show-modal="true" />
  </UIContentWrap>
  <MobileSideNav v-if="app.state.isMobile" :is-loading="isLoading" />
  <div v-if="app.state.showGroupSettings">
    <GroupSettings :show="app.state.showGroupSettings" @update:show="app.state.showGroupSettings = $event" />
  </div>
  <PaymentRedirectionConfirmation />
  <UIConfirmation
    :show="showRouteAccessInfo"
    :height="300"
    type="warning"
    :title="t('common.thisGroupIsPrivate')"
    button-type="primary"
    message="You need to be a member of this group to access group specific information. Please request access by clicking the Join Group button."
    positive-text="OK"
    @update:show="showRouteAccessInfo = $event"
    @update:confirmed="() => (showRouteAccessInfo = false)"
  />

  <div class="chat-windows-container fixed bottom-0 right-10 z-[2] flex gap-4">
    <ChatViewIndex v-for="(chat, index) in activeChats" :key="chat._id" :conversation="chat" :index="index" @close="closeChat(index)" />
  </div>
  <LoginModal
    v-if="appStore.showLoginModal"
    :page="appStore.loginPage"
    :location-data="app.state.locationData"
    :show="appStore.showLoginModal"
    :login-confirmation="handleLoginAction"
    @update:show="appStore.closeLoginModal()"
  />
  <LoginModal
    v-if="showLoginModal"
    :page="appStore.loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="handleLoginAction"
    @update:show="showLoginModal = false"
  />
  <MoveToChannelModal
    :show="showMovePostToChannelModal"
    :post-id="postToMove?._id as string"
    :current-channel="{
      name: postToMove?.channelName,
      _id: postToMove?.channelId,
    }"
    @update:show="showMovePostToChannelModal = false"
    @postUpdated="handlePostUpdated"
  />

  <!-- Global Private Channel Tooltip -->
  <PrivateChannelTooltip />
  <RestrictedEventTooltip />
  <PrivateCourseTooltip />
</template>
<style scoped>
  /* @media screen and (max-width: 1439px) {
  .sidebar {
    display: none;
  }
} */
  .chat-windows-container {
    flex-wrap: nowrap;
    align-items: flex-end;
  }
  .n-divider:not(.n-divider--vertical) {
    margin: 0px;
  }
  @media (min-width: 930px) {
    /* CSS styles specific to larger screens */
    body {
      font-size: 18px;
    }
  }

  @media (max-width: 930px) {
    .outer-container {
      padding: 0px !important;
    }
    .fade-enter-active {
      /* Fade in animation */
      opacity: 1;
      transition: opacity 0.3s ease-in-out !important;
    }

    .fade-leave-active {
      /* Fade out animation */
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
    }
  }
  .hl-wrapper-container {
    padding: 0px !important;
  }
  html,
  body {
    position: relative;
    margin: 0;
    border: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  body {
    overflow: auto;
  }

  @media (min-width: 1280px) {
    .sidebar {
      max-width: 334px;
    }
  }

  @media (min-width: 1450px) {
    .sidebar {
      max-width: 354px;
    }
  }

  @media (max-width: 930px) {
    .outer-container {
      padding: 0px !important;
      padding-bottom: 75px !important;
    }
  }
  .hl-wrapper-container {
    padding: 0px !important;
  }
  .box_shadow_communities_nav {
    box-shadow: 0 1.5px 4px rgba(0, 0, 0, 0.07) !important;
    border-width: 0.5px !important;
    border-top: 0px;
    border-left: 0px;
    border-right: 0px;
  }

  .box_shadow_sidebar {
    box-shadow: 0 1.5px 4px rgba(0, 0, 0, 0.07) !important;
    border-width: 0.5px !important;
  }

  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    transition-duration: 0.3s;
    transition-property: height, opacity, transform;
    transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
    overflow: hidden;
  }

  .slide-left-enter,
  .slide-right-leave-active {
    opacity: 0;
    transform: translate(2em, 0);
  }

  .slide-left-leave-active,
  .slide-right-enter {
    opacity: 0;
    transform: translate(-2em, 0);
  }

  @media (prefers-reduced-motion: reduce) {
    .slide-left-enter-active,
    .slide-left-leave-active,
    .slide-right-enter-active,
    .slide-right-leave-active {
      transition-duration: 0.2s;
      transition-timing-function: linear;
    }
  }
</style>
